// ListPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import FadeTransition from "../../../../../../components/FadeTransition";
import LoadingCircle from "../../../../../../components/LoadingCircle";
import ListPageHeader from "../../../../../../components/Tables/ListPageControls/ListPageHeader";
import PaginationContainer from "./PaginationContainer";
import CustomerTable from "./Table/CustomerTable";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCustomers,
  selectLoading,
  selectAllCustomers,
  fetchAllCustomers,
} from "../../../../../../redux/customers/customerSlice";
import useDebounce from "../../../../../../hooks/useDebounce";
import useAddCustomer from "../../hooks/useCustomerAdd";
import useCustomerDelete from "../../hooks/useCustomerDelete";
import useTableSorting from "../../../../../../hooks/useTableSorting";
import {
  selectCurrentPage,
  selectRowsPerPage,
  setCurrentPage,
} from "../../../../../../redux/customers/customerPaginationSlice";
import AlertSnackbar from "../../../../../../components/Notifications/AlertSnackbar";
import {
  selectedSelectedCustomer,
  setSelectedCustomer,
} from "../../../../../../redux/customers/selectedCustomerSlice";
import ViewModal from "../Modal/ViewModal";
import { fetchAllManagementCompanies } from "../../../../../../redux/mgmtCo/managementSlice";
import { selectCustomers } from "../../../../../../redux/customers/customerSlice";
import useExport from "../../../../../../hooks/useExport";
import useCustomerUpdate from "../../hooks/useCustomerUpdate";
import {
  fetchPaymentTerms,
  fetchTaxDetails,
  fetchSalesPersons,
} from "../../services/customerLookupService";

const ListPage = () => {
  const dispatch = useDispatch();
  const loading = useSelector(selectLoading);
  const currentPage = useSelector(selectCurrentPage);
  const rowsPerPage = useSelector(selectRowsPerPage);
  const selectedCustomer = useSelector(selectedSelectedCustomer);
  // const customers = useSelector(selectCustomers); // select the entire customer list
  const allCustomers = useSelector(selectAllCustomers);
  const allManagementCompanies = useSelector(selectAllCustomers);
  const [dropdownLists, setDropdownLists] = useState({
    allPaymentTerms: [],
    allTaxDetails: [],
    allSalesPersons: [],
  });

  useEffect(() => {
    async function fetchDropdownData() {
      try {
        const paymentTerms = await fetchPaymentTerms();
        const taxDetails = await fetchTaxDetails();
        const salesPersons = await fetchSalesPersons();
        setDropdownLists({
          allPaymentTerms: Array.isArray(paymentTerms) ? paymentTerms : [],
          allTaxDetails: Array.isArray(taxDetails) ? taxDetails : [],
          allSalesPersons: Array.isArray(salesPersons) ? salesPersons : [],
        });
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
      }
    }
    fetchDropdownData();
  }, []);

  // local states
  const [listExpanded, setListExpanded] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [isEditMode, setIsEditMode] = useState(false);

  const { setValidationErrors } = useCustomerUpdate(selectedCustomer);

  // Custom hooks
  const createNewCustomer = useAddCustomer();
  const deleteSelected = useCustomerDelete();
  const { sortField, order, handleSort } = useTableSorting("customerID", "asc");

  // Export hook
  const { exportToCSV } = useExport();

  const handleOpen = () => {
    setValidationErrors({});
    setIsViewOpen(true);
  };

  // Export handler: exports the entire customer list as CSV.
  const handleExport = () => {
    // If your redux state holds only paginated data, you may need to fetch the full list.
    exportToCSV(allCustomers, "customers.csv");
  };

  // Snackbar handler
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleAddSuccess = () => {
    setSnackbar({
      open: true,
      message: "Customer added successfully.",
      severity: "success",
    });
  };

  // Debounced search query
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const handleAddAndView = async () => {
    try {
      const newCustomer = await createNewCustomer();
      if (newCustomer) {
        dispatch(setSelectedCustomer(newCustomer));
        const result = await dispatch(
          fetchCustomers({
            currentPage,
            rowsPerPage,
            searchQuery: "",
            sortField,
            sortOrder: order,
          })
        );
        const newTotalPages = result.payload.totalPages;
        if (currentPage !== newTotalPages) {
          dispatch(setCurrentPage(newTotalPages));
        }
      }
      setIsViewOpen(true);
      setIsEditMode(true);
      handleAddSuccess();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: "error",
      });
    }
  };

  const handleOpenAddFromView = () => {
    handleCloseModal();
    handleAddAndView();
  };

  const updateList = useCallback(() => {
    try {
      // update dependent information here if needed

      dispatch(
        fetchCustomers({
          currentPage,
          rowsPerPage,
          searchQuery: debouncedSearchQuery,
          sortField,
          sortOrder: order,
        })
      );
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: "error",
      });
    }
  }, []);

  const handleDeleteSelected = async () => {
    try {
      await deleteSelected();
      setSnackbar({
        open: true,
        message: "Selected Customer deleted successfully.",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: "error",
      });
    }
  };

  useEffect(() => {
    dispatch(
      fetchCustomers({
        currentPage,
        rowsPerPage,
        searchQuery: debouncedSearchQuery,
        sortField,
        sortOrder: order,
      })
    );
  }, [
    dispatch,
    currentPage,
    rowsPerPage,
    debouncedSearchQuery,
    sortField,
    order,
  ]);

  useEffect(() => {
    if (!allManagementCompanies || allManagementCompanies.length === 0) {
      dispatch(fetchAllManagementCompanies());
    }
  }, [dispatch, allManagementCompanies]);

  useEffect(() => {
    if (!allCustomers || allCustomers.length === 0) {
      dispatch(fetchAllCustomers());
    }
  }, [dispatch, allCustomers]);

  const handleCloseModal = () => {
    setIsViewOpen(false);
    setIsEditMode(false);
    setValidationErrors({});
    setSelectedCustomer(null);
  };

  return (
    <div className="flex flex-col h-full min-h-0 relative">
      {/* Main Content Section */}
      <div className="flex-1 min-h-0 overflow-auto relative bg-gray-50">
        <FadeTransition show={loading}>
          <LoadingCircle />
        </FadeTransition>
        <FadeTransition show={!loading}>
          <div
            className={`transition-all duration-300 ${
              listExpanded ? "w-full" : "w-11/12"
            } mx-auto px-10`}
          >
            {/* Header Section */}
            <ListPageHeader
              title="Customers: "
              onAdd={handleAddAndView}
              toggleFilterModal={() => {}}
              deleteSelected={handleDeleteSelected}
              onSearch={(value) => setSearchQuery(value)}
              isExpanded={listExpanded}
              setIsExpanded={() => setListExpanded(!listExpanded)}
              onExport={handleExport} // Pass the export handler here
            />
            {/* Table Section */}
            <CustomerTable
              handleOpenViewModal={handleOpen}
              expanded={listExpanded}
              sortField={sortField}
              order={order}
              handleSort={handleSort}
              dropdownLists={dropdownLists}
            />
          </div>
        </FadeTransition>
      </div>
      {/* Footer Section */}
      <PaginationContainer />
      {/* Modals */}
      <ViewModal
        open={isViewOpen}
        handleClose={handleCloseModal}
        handleOpenAddFromView={handleOpenAddFromView}
        updateList={updateList}
        isEditMode={isEditMode}
        setIsEditMode={setIsEditMode}
        dropdownLists={dropdownLists}
      />
      {/* Snackbar Notification */}
      <AlertSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
      />
    </div>
  );
};

export default ListPage;
